import { useQuery, useMutation } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from './apiClient';

const useAfipStore = create(
  persist(
    (set, get) => ({
      tokenAfip: null,
      expirationTime: null,
      sign: null,
      genTime: null,

      setTokenAfip: (token, expirationTime, sign) =>
        set({
          tokenAfip: token,
          expirationTime,
          sign,
          genTime: new Date().toISOString(),
        }),

      clearTokenAfip: () =>
        set({
          tokenAfip: null,
          expirationTime: null,
          sign: null,
          genTime: null,
        }),

      isTokenValid: () => {
        const { tokenAfip, expirationTime, genTime } = get();
        const currentTime = new Date();
        const genTimeDate = new Date(genTime);

        return (
          tokenAfip &&
          new Date(expirationTime) > currentTime &&
          currentTime - genTimeDate < 5 * 60 * 1000
        );
      },
    }),
    {
      name: 'afip-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

const afipKeys = {
  all: ['afip'],
  token: () => [...afipKeys.all, 'token'],
};

export const useAfip = () => {
  const { tokenAfip, expirationTime, sign, genTime, setTokenAfip, clearTokenAfip, isTokenValid } =
    useAfipStore();

  const tokenQuery = useQuery({
    queryKey: afipKeys.token(),
    queryFn: async () => {
      if (isTokenValid()) {
        return {
          data: tokenAfip,
          status: 'existing',
        };
      }

      const response = await apiClient.get('afip/connect');
      const { Token, 'Expiration Time': expirationTime, Sing } = response.data;

      setTokenAfip(Token, expirationTime, Sing);

      return {
        data: Token,
        expirationTime,
        sign: Sing,
        status: response.status,
      };
    },
    staleTime: 4 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const refreshTokenMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get('afip/connect');
      return response.data;
    },
    onSuccess: (data) => {
      const { Token, 'Expiration Time': expirationTime, Sing } = data;
      setTokenAfip(Token, expirationTime, Sing);
    },
    onError: () => {
      clearTokenAfip();
    },
  });

  const refreshToken = () => refreshTokenMutation.mutate();

  return {
    tokenAfip: tokenQuery.data?.data || tokenAfip,
    expirationTime,
    sign,
    genTime,
    isLoading: tokenQuery.isLoading || refreshTokenMutation.isPending,
    error: tokenQuery.error || refreshTokenMutation.error,
    isValid: isTokenValid(),
    refreshToken,
    clearTokenAfip,
    refetch: tokenQuery.refetch,
  };
};

const getCaeInfo = async ({ salePointNumber, voucherNumber }) => {
  const res = await apiClient.get(`afip/cae/${salePointNumber}/6/${voucherNumber}`);
  return res.data;
};

export const useCaeInfo = ({ salePointNumber, voucherNumber }, options = {}) => {
  return useQuery({
    queryKey: ['afip', 'cae', salePointNumber, 6, voucherNumber],
    queryFn: () => getCaeInfo({ salePointNumber, voucherNumber }),
    enabled: !!salePointNumber && !!voucherNumber,
    ...options,
  });
};
